import { useQuery } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import moment from "moment";
import { useMemo } from "react";
import { getContractGroupCustomFieldsAsync } from "src/api/contractGroup/contractgroup-customfield-api";
import { CUSTOM_FIELD_TYPE, CustomFieldType } from "src/api/contractGroup/contractgroup-type";
import { HistoryUpdate, HistoryUpdatePatch } from "src/api/history/history-types";
import { useApiOperation } from "src/api/hooks";
import { BaseButton } from "src/components";
import { useModal } from "src/recoil/modalState/hook";
import { ViewYmdFormat } from "src/utils";
import { KeyNamesMap, KeyNamesMapKey } from "./libs";

/** 계약그룹 변경내역 감사 로그 종류 코드 */
const CONTRACT_GROUP_CATEGORY = "AL115";

/**
 * 캘린더 타입 커스텀필드의 입력값은 ISO 문자열로 내려오므로 날짜만 노출한다
 * (캘린더 유형은 시간 입력이 없어 목록과 동일하게 YYYY-MM-DD 로 표기)
 */
const formatCustomFieldValue = (value: any, fieldType?: CustomFieldType) => {
  if (fieldType !== CUSTOM_FIELD_TYPE.CALENDAR) return value;
  if (value === null || value === undefined || value === "") return value;

  // 유효한 날짜가 아니면 원본을 그대로 노출
  return moment(value).isValid() ? moment(value).format(ViewYmdFormat.YYYY_MM_DD) : value;
};

/**
 * 변경 내역 상세 모달
 * @param historyUpdate 변경 내역 데이터
 * @returns 변경 내역 상세 모달
 */
const HistoryDetailModal = ({ historyUpdate }: { historyUpdate: HistoryUpdate }) => {
  const { setAbstractModalZ1 } = useModal();

  // 계약그룹(AL115) 변경내역은 patch 항목에 fieldId/optionId 만 내려오므로
  // 커스텀필드 목록을 조회해 필드명/옵션명으로 치환한다
  const isContractGroupHistory = historyUpdate.category === CONTRACT_GROUP_CATEGORY;
  const { executeAsync: getContractGroupCustomFields } = useApiOperation(getContractGroupCustomFieldsAsync);

  const { data: customFieldList, isPending: isCustomFieldPending } = useQuery({
    queryKey: ["contractGroup_customFieldList_history"],
    enabled: isContractGroupHistory,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await getContractGroupCustomFields();
      return data?.data?.customFieldList ?? [];
    },
  });

  // fieldId → (필드명, 필드유형) / optionId → (소속 필드명, 옵션명) 조회용 맵
  const { fieldInfoMap, optionInfoMap } = useMemo(() => {
    const fieldInfoMap = new Map<number, { fieldName: string; fieldType?: CustomFieldType }>();
    const optionInfoMap = new Map<number, { fieldName: string; optionName: string }>();

    (customFieldList ?? []).forEach((field) => {
      const fieldName = field.fieldName || "";
      fieldInfoMap.set(field.fieldId, { fieldName, fieldType: field.fieldType });

      field.optionList?.forEach((option) => {
        optionInfoMap.set(option.optionId, { fieldName, optionName: option.optionName });
      });
    });

    return { fieldInfoMap, optionInfoMap };
  }, [customFieldList]);

  /**
   * patch 항목에서 커스텀필드 정보를 해석한다
   * - 입력값 변경 건: 최상위 fieldId 사용
   * - 옵션 변경 건: fieldId 가 없고 optionId 만 오므로 optionId 로 소속 필드를 역추적
   * - op 가 add 인 건: 최상위 대신 value 객체 안의 optionId 사용
   */
  const resolveCustomField = (item: HistoryUpdatePatch) => {
    // 조회 완료 전에는 ID 폴백이 잠깐 노출되지 않도록 표시하지 않는다
    if (!isContractGroupHistory || isCustomFieldPending) return null;

    const patchValue = typeof item?.value === "object" && item?.value !== null ? (item.value as Record<string, any>) : undefined;
    const fieldId = item?.fieldId ?? patchValue?.fieldId;
    const optionId = item?.optionId ?? patchValue?.optionId;

    if (fieldId === undefined && optionId === undefined) return null;

    const optionInfo = optionId !== undefined ? optionInfoMap.get(optionId) : undefined;
    const fieldInfo = fieldId !== undefined ? fieldInfoMap.get(fieldId) : undefined;
    const fieldName = fieldInfo?.fieldName || optionInfo?.fieldName;

    return {
      fieldId: fieldId ?? undefined,
      optionId: optionId ?? undefined,
      fieldType: fieldInfo?.fieldType,
      // 커스텀필드가 삭제되어 목록에 없으면 ID 로 폴백
      fieldLabel: fieldName || (fieldId !== undefined ? `ID ${fieldId}` : "-"),
      optionLabel: optionId !== undefined ? optionInfo?.optionName || `ID ${optionId}` : undefined,
    };
  };

  /**
   * 객체를 재귀적으로 순회하며 key-value 형태로 표시하는 컴포넌트
   * - data가 객체가 아니면(기본 타입, null 등) 문자열로 출력
   * - 객체면 각 key-value 쌍을 순회하며 표시, 빈 값(null, 빈 문자열, 빈 객체 등)은 렌더링하지 않음
   * - key는 KeyNamesMap에서 한글 등으로 매핑하여 표시(없으면 원본 key)
   * - value가 객체면 재귀적으로 RenderObject를 다시 호출하여 계층적으로 렌더링
   * - fieldType: 커스텀필드 유형(캘린더 입력값 날짜 포맷에 사용)
   */
  const RenderObject = ({ data, fieldType }: { data: any; fieldType?: CustomFieldType }) => {
    if (typeof data !== "object" || data === null) {
      // 기본 타입(문자열, 숫자 등) 또는 null이면 바로 출력
      return <span>{String(data)}</span>;
    }

    return (
      <div className="object-content">
        {Object.entries(data)?.map(([key, value]) => {
          // key와 value, 그리고 value가 비어있는지 콘솔에 출력(개발용)
          console.log(`${key} : ${value} `, isEmpty(value));
          // KeyNamesMap에서 매핑된 key명 찾기, 없으면 원본 key 사용
          const matchedKeyName = KeyNamesMap[historyUpdate.category as KeyNamesMapKey]?.[key as keyof (typeof KeyNamesMap)[KeyNamesMapKey]] || key;

          // value가 null, 빈 문자열, undefined, 공백, 빈 객체/배열이면 렌더링하지 않음
          if (value === null || value === "" || value === undefined || value === " " || (typeof value === "object" && isEmpty(value))) {
            // 빈 값은 화면에 표시하지 않음
            return null;
          }

          return (
            <div key={key} className="object-item">
              {/* key명(한글 등) 표시 */}
              <span className="object-key">{matchedKeyName}: </span>
              <span className="object-value">
                {/* value가 객체면 재귀적으로 RenderObject 호출 (캘린더 입력값은 날짜로 포맷) */}
                <RenderObject data={key === "inputValue" ? formatCustomFieldValue(value, fieldType) : value} fieldType={fieldType} />
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * 변경 내용을 한 항목씩 표시하는 컴포넌트
   * - item: 변경 내역 데이터(경로, 변경 전/후 값 등)
   * - index: 변경 내역의 순번
   * - 경로(path)는 KeyNamesMap을 통해 한글 등으로 매핑하여 표시
   * - 변경 전(origin), 변경 후(value)는 객체면 RenderObject로 계층적으로 표시
   * - op가 remove면 '삭제됨' 표시
   */
  const ChangeItem = ({ item = "", index }: { item: any; index: number }) => {
    // 경로(path)를 '/'로 분리하여 각 key를 KeyNamesMap에서 한글 등으로 매핑, 숫자는 그대로 표시

    const matchedPathName = item.path
      ?.split("/")
      ?.map((pathKey: string) => {
        if (!isNaN(Number(pathKey))) return pathKey; //숫자면 그대로 표시
        const matchedKeyName = KeyNamesMap[historyUpdate.category as KeyNamesMapKey]?.[pathKey as keyof (typeof KeyNamesMap)[KeyNamesMapKey]];
        return matchedKeyName || pathKey;
      })
      ?.join("/");

    // 계약그룹 커스텀필드 변경 건이면 fieldId/optionId 를 필드명/옵션명으로 해석
    const customField = resolveCustomField(item);

    // 캘린더 유형의 입력값(/inputValue) 변경 건은 ISO 원문 대신 날짜로 노출
    const isInputValuePath = typeof item.path === "string" && item.path.endsWith("/inputValue");
    const displayOrigin = isInputValuePath ? formatCustomFieldValue(item.origin, customField?.fieldType) : item.origin;
    const displayValue = isInputValuePath ? formatCustomFieldValue(item.value, customField?.fieldType) : item.value;

    return (
      <div className="change-item">
        <div className="change-header">
          <span className={`index`}>{index + 1}</span>
          {/* 변경 타입(예: replace, add 등) */}
          <span className={`change-type ${item.op === "replace" ? "type-replace" : "type-add"}`}>{item.op}</span>
          <div className="change-path">
            {/* 매핑된 경로명(한글 등) */}
            <span className="matched-path">
              {matchedPathName}
              {/* 커스텀필드명 / 옵션명 */}
              {customField && (
                <span className="matched-custom-field">
                  {` (${customField.fieldLabel}${customField.optionLabel ? ` > ${customField.optionLabel}` : ""})`}
                </span>
              )}
            </span>
            {/* 원본 경로명 */}
            <span className="original-path">{item.path}</span>
          </div>
        </div>
        <div className="change-content">
          <div className="change-origin">
            <span className="label">변경 전: </span>
            {/* 객체면 RenderObject로 계층적으로 표시, 아니면 문자열로 표시 */}
            {typeof displayOrigin === "object" && displayOrigin !== null ? (
              <RenderObject data={displayOrigin} fieldType={customField?.fieldType} />
            ) : (
              <span>{String(displayOrigin ?? "-")}</span>
            )}
          </div>
          <div className="change-value">
            <span className="label">변경 후: </span>

            {item.op !== "remove" ? (
              <>
                {typeof displayValue === "object" && displayValue !== null ? (
                  <RenderObject data={displayValue} fieldType={customField?.fieldType} />
                ) : (
                  <span>{String(displayValue ?? "-")}</span>
                )}
              </>
            ) : (
              <span>삭제됨</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // 실제 모달의 렌더링 구조
  return (
    <>
      <section className="base-abstract-modal__title">
        <h1>변경내용 상세</h1>
      </section>

      {/* 모달 본문(내용) 영역 */}
      <section className="base-abstract-modal__contents-minmax-height px30">
        <div className="history-detail-container">
          <div className="common-info">
            <div className="info-item">
              <span className="label">변경자</span>
              {/* 변경자 타입에 따라 id 또는 displayName 표시 */}
              {historyUpdate.user?.type === "TAAP" ? (
                <span>{historyUpdate.user?.id || "-"}</span>
              ) : (
                <span>{historyUpdate.user?.displayName || "-"}</span>
              )}
            </div>
            <div className="info-item">
              <span className="label">변경일시</span>
              <span>{moment(historyUpdate.revisionTime).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM) || "-"}</span>
            </div>
            <div className="info-item">
              <span className="label">타입</span>
              <span>{historyUpdate.message?.type || "-"}</span>
            </div>
            <div className="total-changes">총 {historyUpdate.message?.data?.data?.length ?? "-"}개의 변경 사항</div>
          </div>

          {/* 변경 내용 목록: 각 변경 사항을 ChangeItem으로 렌더링 */}
          <div className="changes-list">
            {historyUpdate.message?.data?.data && historyUpdate.message?.data?.data?.length > 0
              ? historyUpdate.message?.data?.data?.map((item, index) => <ChangeItem key={index} item={item} index={index} />)
              : null}
          </div>
        </div>
      </section>

      {/* 모달 하단 버튼 영역 */}
      <section className="base-abstract-modal__btn-wrap">
        <BaseButton title="닫기" className="color-white" onClick={() => setAbstractModalZ1({ isOpen: false })} />
      </section>
    </>
  );
};

export default HistoryDetailModal;
